import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Alert,
  useTheme,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { tokens } from "../../theme";
import { updateAdminSchema } from "../../schema/adminSchema";
import { updateAdmin } from "../../api/admins";
import useMediaQuery from "@mui/material/useMediaQuery";
import ModalContainer from "../models/ModalContainer";

const AdminUpdate = ({ selectedAdmin, openModal, handleModalClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const { adminId } = useParams();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const admin = selectedAdmin;

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("phoneNumber", values.phoneNumber);
      formData.append("age", values.age);
      formData.append("accessLevel", values.accessLevel);

      if (values.profilePicture) {
        formData.append("profilePicture", values.profilePicture);
      }

      const response = await updateAdmin(adminId, formData);
      if (response.status === 200) {
        setAlertMessage("Admin updated successfully!");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          handleModalClose();
        }, 3000);
      }
    } catch (error) {
      setAlertMessage("Failed to update admin.");
      setShowAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContainer
      openModal={openModal}
      setOpenModal={handleModalClose}
      colors={colors}
    >
      <Typography variant="h1" my={3} color="text.primary">
        Update Admin
      </Typography>

      {/* Alert */}
      {showAlert && (
        <Box my={2}>
          <Alert variant="filled" severity="success">
            {alertMessage}
          </Alert>
        </Box>
      )}

      {admin && (
        <Formik
          initialValues={{
            firstName: admin.firstName,
            lastName: admin.lastName,
            email: admin.email,
            phoneNumber: admin.phoneNumber,
            age: admin.age,
            accessLevel: admin.accessLevel,
            profilePicture: null,
          }}
          validationSchema={updateAdminSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="First Name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  name="firstName"
                  error={!!touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Last Name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  name="lastName"
                  error={!!touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  name="email"
                  error={!!touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  sx={{ gridColumn: "span 4" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Phone Number"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.phoneNumber}
                  name="phoneNumber"
                  error={!!touched.phoneNumber && !!errors.phoneNumber}
                  helperText={touched.phoneNumber && errors.phoneNumber}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Age"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.age}
                  name="age"
                  error={!!touched.age && !!errors.age}
                  helperText={touched.age && errors.age}
                  sx={{ gridColumn: "span 2" }}
                />
                <Box
                  gridColumn="span 4"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  {admin.profilePicture && (
                    <img
                      src={`http://localhost:5000/${admin.profilePicture}`}
                      alt="Profile"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        marginBottom: "20px",
                      }}
                    />
                  )}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="file"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    onChange={(event) =>
                      setFieldValue(
                        "profilePicture",
                        event.currentTarget.files[0]
                      )
                    }
                    name="profilePicture"
                    error={!!touched.profilePicture && !!errors.profilePicture}
                    helperText={touched.profilePicture && errors.profilePicture}
                    sx={{
                      "& .MuiInputBase-input": {
                        mb: "10px",
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  Update Admin
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
    </ModalContainer>
  );
};

export default AdminUpdate;
